import { Layout } from "@/components/layout";
import { type Project, useProjects } from "@/lib/queries/use-projects";
import { useSiteContent } from "@/lib/queries/use-site-content";

function ProjectCard({ project }: { project: Project }) {
  const isExternal = project.url?.startsWith("http");

  const content = (
    <>
      <div className="flex items-start justify-between mb-3">
        <p className="text-[10px] text-text-faint tracking-caps uppercase">
          {project.category || "Project"}
        </p>
        <span className="text-text-faint group-hover:text-text-tertiary transition-colors">↗</span>
      </div>
      <h3 className="text-[17px] font-bold text-text-secondary group-hover:text-primary transition-colors mb-2">
        {project.title}
      </h3>
      {project.description && (
        <p className="text-[13px] text-text-tertiary">{project.description}</p>
      )}
    </>
  );

  if (project.url) {
    return (
      <a
        href={project.url}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className="group block p-6 border border-border hover:border-border-hover transition-all bg-card"
      >
        {content}
      </a>
    );
  }

  return <div className="group block p-6 border border-border bg-card">{content}</div>;
}

export function Projects() {
  const { data: projects, isLoading, error } = useProjects();
  const { data: content } = useSiteContent();

  // Content with defaults
  const title = content?.projects_title || "Projects";
  const description =
    content?.projects_description ||
    "Things I've built, companies I've worked at, and experiments I've shipped.";

  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-8 pt-20 pb-16">
        <p className="text-[11px] text-text-muted tracking-wide-caps uppercase mb-6">Portfolio</p>
        <h1 className="text-[44px] font-bold text-primary leading-[1.15] mb-4 tracking-tight">
          {title}
        </h1>
        <div
          className="text-[15px] text-text-secondary leading-relaxed max-w-lg mb-12 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:text-primary [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:opacity-80"
          dangerouslySetInnerHTML={{ __html: description }}
        />

        {isLoading && (
          <div className="py-12">
            <p className="text-text-muted text-center">Loading projects...</p>
          </div>
        )}

        {error && (
          <div className="py-12">
            <p className="text-destructive text-center">
              Failed to load projects. Please try again later.
            </p>
          </div>
        )}

        {projects && projects.length === 0 && (
          <div className="py-12">
            <p className="text-text-muted text-center">No projects yet. Check back soon.</p>
          </div>
        )}

        {projects && projects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
